import React, { useState } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
  GET_CATALOGS,
  GET_LABELS,
  GET_USERS,
  CREATE_CATALOG,
  UPDATE_CATALOG,
  DELETE_CATALOG,
  CREATE_LABEL,
  UPDATE_LABEL,
  DELETE_LABEL,
  DELETE_USER,
  CREATE_USER_ADMIN,
  ADMIN_UPDATE_USER,
  TOGGLE_USER_ENABLED,
  BULK_CREATE_CLIENTS,
  BULK_CREATE_RESOURCES,
  BULK_CREATE_PROJECTS,
  EXPORT_DATA,
  IMPORT_DATA
} from '../graphql/queries';
import { useAuth } from '../context/AuthContext';
import {
  Plus,
  X,
  Edit2,
  Trash2,
  Tag,
  Layers,
  AlertCircle,
  CheckCircle,
  BarChart2,
  Users,
  Briefcase,
  Building,
  UserCheck,
  Database,
  Download,
  Upload,
  FileSpreadsheet,
  Info,
  Shield,
  Eye,
  EyeOff,
  UserPlus,
  Lock,
  Mail
} from 'lucide-react';
import * as XLSX from 'xlsx';

const catalogTypes = [
  { type: 'projectType', label: 'Project Types', icon: Layers },
  { type: 'priority', label: 'Priorities', icon: AlertCircle },
  { type: 'projectStatus', label: 'Project Statuses', icon: CheckCircle },
  { type: 'size', label: 'Sizes', icon: BarChart2 },
  { type: 'clientType', label: 'Client Types', icon: Users },
  { type: 'clientIndustry', label: 'Industries', icon: Building },
  { type: 'clientStatus', label: 'Client Statuses', icon: UserCheck },
  { type: 'resourceSkill', label: 'Resource Skills', icon: Briefcase },
  { type: 'resourceLevel', label: 'Resource Levels', icon: BarChart2 },
];


import { User, Catalog, Label } from '../types';

interface FormData {
  name: string;
  order: number;
  color: string;
  weightJunior: number | string;
  weightMid: number | string;
  weightSenior: number | string;
}

function Settings() {
  const [activeTab, setActiveTab] = useState('projectType');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Catalog | Label | null>(null);
  const [formData, setFormData] = useState<FormData>({ 
    name: '', 
    order: 0, 
    color: '#4f46e5',
    weightJunior: 0,
    weightMid: 0,
    weightSenior: 0
  });
  const [isLabel, setIsLabel] = useState(false);
  const { user: currentUser } = useAuth();

  const { data: usersData, refetch: refetchUsers } = useQuery<{ users: User[] }>(GET_USERS, {
    skip: activeTab !== 'users'
  });

  const [deleteUser] = useMutation(DELETE_USER, {
    onCompleted: () => refetchUsers()
  });

  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'developer' });
  const [userError, setUserError] = useState<string | null>(null);

  const [createUserAdmin] = useMutation(CREATE_USER_ADMIN, {
    onCompleted: () => { setShowUserModal(false); setEditingUser(null); setUserError(null); refetchUsers(); },
    onError: (err) => setUserError(err.message)
  });

  const [adminUpdateUser] = useMutation(ADMIN_UPDATE_USER, {
    onCompleted: () => { setShowUserModal(false); setEditingUser(null); setUserError(null); refetchUsers(); },
    onError: (err) => setUserError(err.message)
  });

  const [toggleUserEnabled] = useMutation(TOGGLE_USER_ENABLED, {
    onCompleted: () => refetchUsers(),
    onError: (err) => alert(err.message)
  });

  const openUserCreateModal = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', password: '', role: 'developer' });
    setUserError(null);
    setShowUserModal(true);
  };

  const openUserEditModal = (user: User) => {
    setEditingUser(user);
    setUserForm({ name: user.name, email: user.email, password: '', role: user.role });
    setUserError(null);
    setShowUserModal(true);
  };

  const handleUserSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setUserError(null);
    if (editingUser) {
      adminUpdateUser({
        variables: {
          id: editingUser.id,
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
          password: userForm.password || null
        }
      });
    } else {
      if (!userForm.password || userForm.password.length < 3) {
        setUserError('Password must be at least 3 characters');
        return;
      }
      createUserAdmin({
        variables: {
          name: userForm.name,
          email: userForm.email,
          password: userForm.password,
          role: userForm.role
        }
      });
    }
  };

  const handleToggleEnabled = (user: User) => {
    if (currentUser?.id === user.id) {
      alert('No puedes deshabilitar tu propia cuenta.');
      return;
    }
    toggleUserEnabled({
      variables: { id: user.id, enabled: !user.enabled }
    });
  };

  const getRoleBadgeStyle = (role: string) => {
    switch (role) {
      case 'admin': return { background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)' };
      case 'project_manager': return { background: 'rgba(79, 70, 229, 0.1)', color: '#4f46e5', border: '1px solid rgba(79, 70, 229, 0.2)' };
      case 'developer': return { background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.2)' };
      case 'general_manager': return { background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.2)' };
      default: return { background: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', border: '1px solid rgba(107, 114, 128, 0.2)' };
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'project_manager': return 'Project Manager';
      case 'developer': return 'Developer';
      case 'general_manager': return 'General Manager';
      default: return role;
    }
  };

  const { data: catalogData, refetch: refetchCatalog } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, {
    variables: { type: activeTab },
    skip: activeTab === 'labels' || activeTab === 'users'
  });

  const { data: labelsData, refetch: refetchLabels } = useQuery<{ labels: Label[] }>(GET_LABELS, {
    skip: activeTab !== 'labels'
  });

  const [createCatalog] = useMutation(CREATE_CATALOG, {
    onCompleted: () => { closeModal(); refetchCatalog(); }
  });

  const [updateCatalog] = useMutation(UPDATE_CATALOG, {
    onCompleted: () => { closeModal(); refetchCatalog(); }
  });

  const [deleteCatalog] = useMutation(DELETE_CATALOG, {
    onCompleted: () => refetchCatalog()
  });

  const [createLabel] = useMutation(CREATE_LABEL, {
    onCompleted: () => { closeModal(); refetchLabels(); }
  });

  const [updateLabel] = useMutation(UPDATE_LABEL, {
    onCompleted: () => { closeModal(); refetchLabels(); }
  });

  const [deleteLabel] = useMutation(DELETE_LABEL, {
    onCompleted: () => refetchLabels()
  });

  const openCreateModal = (forLabel = false) => {
    setEditingItem(null);
    setIsLabel(forLabel);
    setFormData({ 
      name: '', 
      order: 0, 
      color: '#4f46e5',
      weightJunior: 0,
      weightMid: 0,
      weightSenior: 0
    });
    setShowModal(true);
  };

  const openEditModal = (item: Catalog | Label, forLabel = false) => {
    setEditingItem(item);
    setIsLabel(forLabel);
    setFormData({
      name: item.name,
      order: 'order' in item ? item.order || 0 : 0,
      color: item.color || '#4f46e5',
      weightJunior: 'weightJunior' in item ? item.weightJunior || 0 : 0,
      weightMid: 'weightMid' in item ? item.weightMid || 0 : 0,
      weightSenior: 'weightSenior' in item ? item.weightSenior || 0 : 0
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isLabel) {
      if (editingItem) {
        updateLabel({ variables: { id: editingItem.id, input: { name: formData.name, color: formData.color } } });
      } else {
        createLabel({ variables: { input: { name: formData.name, color: formData.color } } });
      }
    } else {
      const input: any = {
        type: activeTab,
        name: formData.name,
        order: parseInt(String(formData.order)) || 0,
        color: formData.color
      };
      if (activeTab === 'size') {
        input.weightJunior = parseFloat(String(formData.weightJunior)) || 0;
        input.weightMid = parseFloat(String(formData.weightMid)) || 0;
        input.weightSenior = parseFloat(String(formData.weightSenior)) || 0;
      }
      if (editingItem) {
        updateCatalog({ variables: { id: editingItem.id, input } });
      } else {
        createCatalog({ variables: { input } });
      }
    }
  };

  const handleDelete = (id: string, forLabel = false) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      if (forLabel) {
        deleteLabel({ variables: { id } });
      } else {
        deleteCatalog({ variables: { id } });
      }
    }
  };

  const handleDeleteUser = (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUser({ variables: { id } });
    }
  };

  const currentType = catalogTypes.find(t => t.type === activeTab);
  const items: any[] = activeTab === 'labels' ? labelsData?.labels || [] : (activeTab === 'users' ? usersData?.users || [] : catalogData?.catalogs || []);


  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
      </div>

      <div className="page-content">
        <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '24px' }}>
          {/* Sidebar */}
          <div className="card" style={{ padding: '16px', height: 'fit-content' }}>
            <h3 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '12px' }}>
              Catalogs
            </h3>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {catalogTypes.map(({ type, label, icon: Icon }) => (
                <button
                  key={type}
                  className={`nav-item ${activeTab === type ? 'active' : ''}`}
                  onClick={() => setActiveTab(type)}
                  style={{ border: 'none' }}
                >
                  <Icon size={18} />
                  {label}
                </button>
              ))}
              <button
                className={`nav-item ${activeTab === 'labels' ? 'active' : ''}`}
                onClick={() => setActiveTab('labels')}
                style={{ border: 'none', marginTop: '8px' }}
              >
                <Tag size={18} />
                Labels
              </button>
              {currentUser?.role === 'admin' && (
                <button
                  className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
                  onClick={() => setActiveTab('users')}
                  style={{ border: 'none', marginTop: '4px' }}
                >
                  <Users size={18} />
                  Users
                </button>
              )}
              <button
                className={`nav-item ${activeTab === 'import' ? 'active' : ''}`}
                onClick={() => setActiveTab('import')}
                style={{ border: 'none', marginTop: '12px' }}
              >
                <Database size={18} />
                Importar Datos
              </button>
              <button
                className={`nav-item ${activeTab === 'backup' ? 'active' : ''}`}
                onClick={() => setActiveTab('backup')}
                style={{ border: 'none', marginTop: '4px' }}
              >
                <Download size={18} />
                Backup & Restore
              </button>
            </nav>
          </div>

          {/* Content */}
          {activeTab === 'import' ? (
            <BulkImporter />
          ) : activeTab === 'backup' ? (
            <BackupRestorePanel />
          ) : activeTab === 'users' ? (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: 600 }}>User Management</h2>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>Create, edit, and manage user accounts and roles.</p>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={openUserCreateModal}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <UserPlus size={16} /> New User
                </button>
              </div>

              <div className="table-container" style={{ border: 'none' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(usersData?.users || []).map((user: User) => (
                      <tr key={user.id} style={{ opacity: user.enabled === false ? 0.55 : 1 }}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                background: user.enabled === false ? 'var(--border)' : 'var(--primary)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '13px',
                                fontWeight: 600,
                                flexShrink: 0
                              }}
                            >
                              {user.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </div>
                            <span style={{ fontWeight: 500 }}>{user.name}</span>
                          </div>
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{user.email}</td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              ...getRoleBadgeStyle(user.role),
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600
                            }}
                          >
                            <Shield size={11} style={{ marginRight: '4px', verticalAlign: '-1px' }} />
                            {getRoleLabel(user.role)}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => handleToggleEnabled(user)}
                            disabled={currentUser?.id === user.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              border: 'none',
                              cursor: currentUser?.id === user.id ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              fontWeight: 600,
                              background: user.enabled !== false ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                              color: user.enabled !== false ? '#10b981' : '#ef4444',
                              transition: 'all 0.2s ease'
                            }}
                            title={currentUser?.id === user.id ? 'No puedes deshabilitar tu propia cuenta' : (user.enabled !== false ? 'Click to disable' : 'Click to enable')}
                          >
                            {user.enabled !== false ? <Eye size={13} /> : <EyeOff size={13} />}
                            {user.enabled !== false ? 'Enabled' : 'Disabled'}
                          </button>
                        </td>
                        <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <button
                              className="btn-icon"
                              onClick={() => openUserEditModal(user)}
                              title="Edit user"
                            >
                              <Edit2 size={15} />
                            </button>
                            <button
                              className="btn-icon"
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={currentUser?.id === user.id}
                              title={currentUser?.id === user.id ? 'No puedes eliminar tu propia cuenta' : 'Delete user'}
                              style={{ opacity: currentUser?.id === user.id ? 0.3 : 1 }}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {(!usersData?.users || usersData.users.length === 0) && (
                  <div className="empty-state">
                    <UserPlus size={40} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
                    <h3>No users found</h3>
                    <p>Create your first user account to get started.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 600 }}>
                  {activeTab === 'labels' ? 'Labels' : currentType?.label}
                </h2>
                <button
                  className="btn btn-primary"
                  onClick={() => openCreateModal(activeTab === 'labels')}
                >
                  <Plus size={18} />
                  Add {activeTab === 'labels' ? 'Label' : 'Item'}
                </button>
              </div>

              <div className="table-container" style={{ border: 'none' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      {activeTab !== 'labels' && <th>Order</th>}
                      {activeTab === 'size' && (
                        <>
                          <th>Junior (%)</th>
                          <th>Mid (%)</th>
                          <th>Senior (%)</th>
                        </>
                      )}
                      <th>Color</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items?.map((item) => (
                      <tr key={item.id}>
                        <td style={{ fontWeight: 500 }}>{item.name}</td>
                        {activeTab !== 'labels' && <td>{item.order || 0}</td>}
                        {activeTab === 'size' && (
                          <>
                            <td>{item.weightJunior !== undefined && item.weightJunior !== null ? item.weightJunior : 0}%</td>
                            <td>{item.weightMid !== undefined && item.weightMid !== null ? item.weightMid : 0}%</td>
                            <td>{item.weightSenior !== undefined && item.weightSenior !== null ? item.weightSenior : 0}%</td>
                          </>
                        )}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '4px',
                                background: item.color || '#3b82f6'
                              }}
                            />
                            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                              {item.color || '#3b82f6'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <button
                              className="btn-icon"
                              onClick={() => openEditModal(item, activeTab === 'labels')}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              className="btn-icon"
                              onClick={() => handleDelete(item.id, activeTab === 'labels')}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {(!items || items.length === 0) && (
                  <div className="empty-state">
                    <h3>No items found</h3>
                    <p>Add your first item to this catalog</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit' : 'Add'} {isLabel ? 'Label' : 'Item'}</h2>
              <button className="btn-icon" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                {!isLabel && (
                  <div className="form-group">
                    <label className="form-label">Order</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Color</label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      style={{ width: '50px', height: '40px', border: 'none', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      className="form-input"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                {activeTab === 'size' && (
                  <>
                    <div className="form-group" style={{ marginTop: '16px' }}>
                      <label className="form-label">Junior Weight (%)</label>
                      <input
                        type="number"
                        className="form-input"
                        step="0.01"
                        value={formData.weightJunior}
                        onChange={(e) => setFormData({ ...formData, weightJunior: e.target.value })}
                        min={0}
                        max={100}
                      />
                    </div>
                    <div className="form-group" style={{ marginTop: '12px' }}>
                      <label className="form-label">Mid Weight (%)</label>
                      <input
                        type="number"
                        className="form-input"
                        step="0.01"
                        value={formData.weightMid}
                        onChange={(e) => setFormData({ ...formData, weightMid: e.target.value })}
                        min={0}
                        max={100}
                      />
                    </div>
                    <div className="form-group" style={{ marginTop: '12px' }}>
                      <label className="form-label">Senior Weight (%)</label>
                      <input
                        type="number"
                        className="form-input"
                        step="0.01"
                        value={formData.weightSenior}
                        onChange={(e) => setFormData({ ...formData, weightSenior: e.target.value })}
                        min={0}
                        max={100}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create User'}</h2>
              <button className="btn-icon" onClick={() => setShowUserModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleUserSubmit}>
              <div className="modal-body" style={{ padding: '24px' }}>
                {userError && (
                  <div style={{
                    padding: '10px 14px',
                    background: 'rgba(239, 68, 68, 0.08)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    borderRadius: '8px',
                    color: '#ef4444',
                    fontSize: '13px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <AlertCircle size={15} />
                    {userError}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Users size={14} /> Full Name *
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    placeholder="e.g. John Doe"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Mail size={14} /> Email *
                  </label>
                  <input
                    type="email"
                    className="form-input"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Lock size={14} /> Password {editingUser ? '' : '*'}
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder={editingUser ? 'Leave blank to keep current password' : 'Enter a secure password'}
                    required={!editingUser}
                    minLength={3}
                  />
                  {editingUser && (
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                      Only fill this if you want to change the password.
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Shield size={14} /> Role *
                  </label>
                  <select
                    className="form-input"
                    value={userForm.role}
                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    required
                  >
                    <option value="admin">Admin</option>
                    <option value="project_manager">Project Manager</option>
                    <option value="developer">Developer</option>
                    <option value="general_manager">General Manager</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '16px 24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowUserModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function BackupRestorePanel() {
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState<string | null>(null);

  const [exportData, { loading: exporting }] = useLazyQuery(EXPORT_DATA, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data && data.exportData) {
        try {
          const blob = new Blob([data.exportData], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const dateStr = new Date().toISOString().slice(0, 10);
          a.download = `backup_pm_tools_${dateStr}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setSuccessMsg('Base de datos exportada exitosamente como archivo JSON.');
          setError(null);
        } catch (err: any) {
          setError(`Error al descargar el archivo: ${err.message}`);
        }
      }
    },
    onError: (err) => {
      setError(`Error al exportar los datos del servidor: ${err.message}`);
    }
  });

  const [importData, { loading: importing }] = useMutation(IMPORT_DATA, {
    onCompleted: (data) => {
      if (data && data.importData) {
        setSuccessMsg('Base de datos restaurada con éxito. Recargando la aplicación...');
        setError(null);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    },
    onError: (err) => {
      setError(`Error al importar los datos al servidor: ${err.message}`);
    }
  });

  const handleExport = () => {
    setError(null);
    setSuccessMsg(null);
    exportData();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setFileName(file.name);
    setError(null);
    setSuccessMsg(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const content = evt.target?.result as string;
      try {
        // Validate JSON format
        const parsed = JSON.parse(content);
        // Basic check for required arrays
        if (!parsed.catalogs && !parsed.labels && !parsed.clients && !parsed.resources && !parsed.projects) {
          setError('El archivo JSON no parece ser una copia de seguridad válida. Falta estructura de datos.');
          setFileContent(null);
          return;
        }
        setFileContent(content);
      } catch (err) {
        setError('El archivo seleccionado no es un archivo JSON válido.');
        setFileContent(null);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    if (!fileContent) {
      setError('Por favor, selecciona un archivo JSON de copia de seguridad válido primero.');
      return;
    }

    if (window.confirm('¿Estás seguro de que deseas restaurar la base de datos? Esto borrará permanentemente todos los datos existentes (excepto usuarios) y los reemplazará con la información del archivo.')) {
      setError(null);
      setSuccessMsg(null);
      importData({ variables: { jsonData: fileContent } });
    }
  };

  const handleCancel = () => {
    setFileName('');
    setFileContent(null);
    setError(null);
    setSuccessMsg(null);
  };

  const isLoading = exporting || importing;

  return (
    <div className="card" style={{ padding: '24px' }}>
      <div>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>Copia de Seguridad y Restauración (Backup & Restore)</h2>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '24px' }}>
          Exporta todos los datos del sistema en un único archivo JSON para respaldar la información o transferirla a otra instancia.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Export Card */}
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: '8px',
          padding: '20px',
          background: 'var(--surface)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Download size={18} style={{ color: 'var(--primary)' }} />
              Exportar Datos
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: '1.5' }}>
              Genera y descarga un archivo JSON completo que incluye catálogos, etiquetas, clientes, recursos y proyectos. No incluye usuarios ni sesiones.
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleExport}
            disabled={isLoading}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          >
            {exporting ? 'Generando archivo...' : 'Exportar Base de Datos (JSON)'}
          </button>
        </div>

        {/* Import Card */}
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: '8px',
          padding: '20px',
          background: 'var(--surface)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Upload size={18} style={{ color: 'var(--primary)' }} />
              Importar Datos
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: '1.5' }}>
              Restaura la configuración e información a partir de una copia de seguridad JSON previamente exportada.
            </p>
          </div>
          <div>
            {fileName ? (
              <div style={{
                background: 'var(--background)',
                borderRadius: '6px',
                padding: '10px 12px',
                border: '1px solid var(--border)',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                  📄 {fileName}
                </span>
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="json-backup-upload"
                  disabled={isLoading}
                />
                <label
                  htmlFor="json-backup-upload"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    border: '1.5px dashed var(--border)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    textAlign: 'center',
                    background: 'var(--background)'
                  }}
                >
                  Seleccionar copia de seguridad (.json)
                </label>
              </div>
            )}
            <button
              className="btn btn-secondary"
              onClick={handleImport}
              disabled={!fileContent || isLoading}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                border: '1px solid var(--border)',
                background: fileContent ? 'var(--primary)' : 'var(--background)',
                color: fileContent ? 'white' : 'var(--text-muted)'
              }}
            >
              {importing ? 'Importando datos...' : 'Restaurar Base de Datos'}
            </button>
          </div>
        </div>
      </div>

      {/* Warning Notice */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '16px',
        background: 'rgba(239, 68, 68, 0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        fontSize: '13px',
        color: 'var(--text-secondary)',
        marginBottom: '16px'
      }}>
        <AlertCircle size={20} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: '2px' }} />
        <div>
          <strong style={{ color: 'var(--danger-text)' }}>ADVERTENCIA DE SOBRESCRITURA:</strong> Al realizar una restauración se eliminarán permanentemente todos los datos existentes de catálogos, etiquetas, clientes, recursos y proyectos antes de importar el nuevo archivo. Los usuarios registrados actuales y las sesiones no se verán modificados.
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          padding: '12px 16px',
          background: 'var(--danger-light)',
          color: 'var(--danger-text)',
          borderRadius: '6px',
          fontSize: '13px',
          marginTop: '16px'
        }}>
          <AlertCircle size={18} />
          <div>{error}</div>
        </div>
      )}

      {successMsg && (
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          padding: '12px 16px',
          background: 'var(--success-light)',
          color: 'var(--success-text)',
          borderRadius: '6px',
          fontSize: '13px',
          marginTop: '16px'
        }}>
          <CheckCircle size={18} />
          <div>{successMsg}</div>
        </div>
      )}
    </div>
  );
}

interface ParsedRow {
  isValid: boolean;
  errors: string[];
  data: any;
  original: any;
}

function BulkImporter() {
  const [selectedEntity, setSelectedEntity] = useState<'clients' | 'resources' | 'projects'>('clients');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [bulkCreateClients, { loading: loadingClients }] = useMutation(BULK_CREATE_CLIENTS);
  const [bulkCreateResources, { loading: loadingResources }] = useMutation(BULK_CREATE_RESOURCES);
  const [bulkCreateProjects, { loading: loadingProjects }] = useMutation(BULK_CREATE_PROJECTS);

  const isLoading = loadingClients || loadingResources || loadingProjects;

  const parseDate = (val: any): string | null => {
    if (!val) return null;
    if (val instanceof Date) {
      if (isNaN(val.getTime())) return null;
      return val.toISOString();
    }
    if (typeof val === 'number') {
      const date = new Date((val - 25569) * 86400 * 1000);
      return date.toISOString();
    }
    const parsed = Date.parse(val);
    if (!isNaN(parsed)) {
      return new Date(parsed).toISOString();
    }
    return null;
  };

  const validateRow = (row: any, entity: string): ParsedRow => {
    const errors: string[] = [];
    let mapped: any = {};

    if (entity === 'clients') {
      const name = String(row['Nombre'] || '').trim();
      const email = String(row['Email'] || '').trim();
      const company = String(row['Empresa'] || '').trim();
      const country = String(row['Pais'] || '').trim();
      const industry = String(row['Industria'] || '').trim();
      const status = String(row['Estado'] || 'Prospect').trim();

      if (!name) errors.push('El Nombre es requerido.');
      if (!company) errors.push('La Empresa es requerida.');
      if (!email) {
        errors.push('El Email es requerido.');
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        errors.push('El formato de Email es inválido.');
      }

      mapped = { name, email, company, country, industry, status };
    } else if (entity === 'resources') {
      const name = String(row['Nombre'] || '').trim();
      const email = String(row['Email'] || '').trim();
      const skill = String(row['Skill'] || '').trim();
      const level = String(row['Nivel'] || '').trim();
      const loadVal = row['Carga'] !== undefined ? row['Carga'] : row['Disponibilidad'];
      let load = 0;

      if (!name) errors.push('El Nombre es requerido.');
      if (!email) {
        errors.push('El Email es requerido.');
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        errors.push('El formato de Email es inválido.');
      }

      if (loadVal !== undefined && loadVal !== '') {
        const parsedLoad = parseFloat(String(loadVal));
        if (isNaN(parsedLoad)) {
          errors.push('La carga debe ser un número.');
        } else {
          load = parsedLoad;
        }
      }

      mapped = { name, email, skill, level, load };
    } else {
      const name = String(row['Nombre'] || '').trim();
      const description = String(row['Descripcion'] || '').trim();
      const clientName = String(row['Cliente'] || '').trim();
      const type = String(row['Tipo'] || '').trim();
      const status = String(row['Estado'] || 'Backlog').trim();
      const priority = String(row['Prioridad'] || 'Medium').trim();
      const size = String(row['Tamano'] || '').trim();
      const estimate = String(row['Estimacion'] || '').trim();
      const rawStartDate = row['Fecha Inicio'];
      const rawDueDate = row['Fecha Fin'];
      const region = String(row['Region'] || '').trim();
      const groupName = String(row['Grupo'] || '').trim();
      const devsStr = String(row['Recursos'] || '');
      const devsArray = devsStr ? devsStr.split(',').map((d: string) => d.trim()).filter(Boolean) : [];

      if (!name) errors.push('El Nombre del proyecto es requerido.');

      let startDate = null;
      if (rawStartDate) {
        startDate = parseDate(rawStartDate);
        if (!startDate) errors.push('Fecha Inicio inválida.');
      }

      let dueDate = null;
      if (rawDueDate) {
        dueDate = parseDate(rawDueDate);
        if (!dueDate) errors.push('Fecha Fin inválida.');
      }

      mapped = {
        name,
        description,
        clientName,
        type,
        status,
        priority,
        size,
        estimate,
        startDate,
        dueDate,
        region,
        groupName,
        developers: devsArray
      };
    }

    return {
      original: row,
      data: mapped,
      isValid: errors.length === 0,
      errors
    };
  };

  const downloadTemplate = (entityType: string) => {
    let headers: string[] = [];
    let sampleData: any[] = [];
    let nameFile = '';

    if (entityType === 'clients') {
      headers = ['Nombre', 'Email', 'Empresa', 'Pais', 'Industria', 'Estado'];
      sampleData = [
        { 'Nombre': 'Juan Pérez', 'Email': 'juan@acme.com', 'Empresa': 'Acme Corp', 'Pais': 'México', 'Industria': 'Software', 'Estado': 'Active' },
        { 'Nombre': 'Sarah Connor', 'Email': 'sarah@skynet.com', 'Empresa': 'Cyberdyne Systems', 'Pais': 'USA', 'Industria': 'Robotics', 'Estado': 'Prospect' }
      ];
      nameFile = 'plantilla_clientes.xlsx';
    } else if (entityType === 'resources') {
      headers = ['Nombre', 'Email', 'Skill', 'Nivel', 'Disponibilidad'];
      sampleData = [
        { 'Nombre': 'Carlos Gómez', 'Email': 'carlos@company.com', 'Skill': 'React', 'Nivel': 'Senior', 'Disponibilidad': 100 },
        { 'Nombre': 'Ana Martínez', 'Email': 'ana@company.com', 'Skill': 'Node.js', 'Nivel': 'Mid', 'Disponibilidad': 80 }
      ];
      nameFile = 'plantilla_recursos.xlsx';
    } else {
      headers = ['Nombre', 'Descripcion', 'Cliente', 'Tipo', 'Estado', 'Prioridad', 'Tamano', 'Estimacion', 'Fecha Inicio', 'Fecha Fin', 'Region', 'Grupo', 'Recursos'];
      sampleData = [
        {
          'Nombre': 'Portal de E-commerce',
          'Descripcion': 'Desarrollo de tienda virtual con pasarela de pagos.',
          'Cliente': 'Acme Corp',
          'Tipo': 'Fixed Price',
          'Estado': 'Active',
          'Prioridad': 'High',
          'Tamano': 'M',
          'Estimacion': '3 meses',
          'Fecha Inicio': '2026-06-01',
          'Fecha Fin': '2026-09-01',
          'Region': 'LATAM',
          'Grupo': 'E-commerce',
          'Recursos': 'Carlos Gómez, Ana Martínez'
        }
      ];
      nameFile = 'plantilla_proyectos.xlsx';
    }

    const ws = XLSX.utils.json_to_sheet(sampleData, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, nameFile);
  };

  const parseExcelFile = (file: File) => {
    setFileName(file.name);
    setError(null);
    setSuccessMsg(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary', cellDates: true });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];
        
        const rawRows = XLSX.utils.sheet_to_json<any>(ws, { defval: '' });
        
        if (rawRows.length === 0) {
          setError('El archivo de Excel está vacío.');
          setParsedRows([]);
          return;
        }

        const firstRow = rawRows[0];
        const keys = Object.keys(firstRow);
        
        let requiredHeader = 'Nombre';
        if (!keys.includes(requiredHeader)) {
          setError(`El formato de la plantilla parece incorrecto. No se encontró la columna requerida: "${requiredHeader}". Asegúrate de descargar la plantilla correcta.`);
          setParsedRows([]);
          return;
        }

        const validated = rawRows.map((row) => validateRow(row, selectedEntity));
        setParsedRows(validated);
      } catch (err) {
        setError('Ocurrió un error al analizar el archivo. Comprueba que sea un archivo de Excel válido.');
        setParsedRows([]);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      parseExcelFile(file);
    }
  };

  const handleConfirmImport = async () => {
    const validRows = parsedRows.filter(r => r.isValid).map(r => r.data);
    if (validRows.length === 0) {
      setError('No hay registros válidos para importar.');
      return;
    }

    try {
      if (selectedEntity === 'clients') {
        await bulkCreateClients({ variables: { input: validRows } });
      } else if (selectedEntity === 'resources') {
        await bulkCreateResources({ variables: { input: validRows } });
      } else {
        await bulkCreateProjects({ variables: { input: validRows } });
      }

      setSuccessMsg(`Se importaron exitosamente ${validRows.length} registros de ${selectedEntity === 'clients' ? 'Clientes' : selectedEntity === 'resources' ? 'Recursos' : 'Proyectos'}.`);
      setParsedRows([]);
      setFileName('');
    } catch (err: any) {
      setError(`Error durante la importación: ${err.message || 'Error desconocido'}`);
    }
  };

  const handleCancel = () => {
    setParsedRows([]);
    setFileName('');
    setError(null);
    setSuccessMsg(null);
  };

  const getHeaders = () => {
    if (selectedEntity === 'clients') {
      return ['Nombre', 'Email', 'Empresa', 'Pais', 'Industria', 'Estado'];
    } else if (selectedEntity === 'resources') {
      return ['Nombre', 'Email', 'Skill', 'Nivel', 'Disponibilidad'];
    } else {
      return ['Nombre', 'Descripcion', 'Cliente', 'Tipo', 'Estado', 'Prioridad', 'Tamano', 'Estimacion', 'Fecha Inicio', 'Fecha Fin', 'Region', 'Grupo', 'Recursos'];
    }
  };

  const headers = getHeaders();
  const validCount = parsedRows.filter(r => r.isValid).length;
  const invalidCount = parsedRows.length - validCount;

  return (
    <div className="card" style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>Importación Masiva de Datos</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Importa múltiples clientes, recursos o proyectos a la vez utilizando un archivo Excel.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
        <button
          className={`btn ${selectedEntity === 'clients' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setSelectedEntity('clients'); handleCancel(); }}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', border: 'none' }}
        >
          <Building size={16} />
          Clientes
        </button>
        <button
          className={`btn ${selectedEntity === 'resources' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setSelectedEntity('resources'); handleCancel(); }}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', border: 'none' }}
        >
          <Users size={16} />
          Recursos
        </button>
        <button
          className={`btn ${selectedEntity === 'projects' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setSelectedEntity('projects'); handleCancel(); }}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', border: 'none' }}
        >
          <Layers size={16} />
          Proyectos
        </button>
      </div>

      {/* Info Warning */}
      {selectedEntity === 'projects' && (
        <div style={{
          display: 'flex',
          gap: '12px',
          padding: '16px',
          background: 'rgba(79, 70, 229, 0.05)',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid rgba(79, 70, 229, 0.2)',
          fontSize: '13px',
          color: 'var(--text-secondary)'
        }}>
          <Info size={20} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong style={{ color: 'var(--text-primary)' }}>Mapeo automático de Relaciones:</strong><br />
            Para los proyectos, introduce el nombre exacto de la empresa del cliente en la columna <code style={{ background: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '4px' }}>Cliente</code> y los nombres de los recursos separados por comas en la columna <code style={{ background: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '4px' }}>Recursos</code>. El sistema resolverá automáticamente sus IDs de base de datos.
          </div>
        </div>
      )}

      {/* Download Template Banner */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        marginBottom: '24px',
        background: 'var(--surface)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FileSpreadsheet size={32} style={{ color: 'var(--success)' }} />
          <div>
            <h4 style={{ fontWeight: 600, fontSize: '14px' }}>¿No tienes la plantilla oficial?</h4>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Descarga la plantilla con el formato requerido y rellena los datos de tu importación.
            </p>
          </div>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => downloadTemplate(selectedEntity)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid var(--border)' }}
        >
          <Download size={16} />
          Descargar Plantilla
        </button>
      </div>

      {/* Drag & Drop Upload Zone */}
      {parsedRows.length === 0 ? (
        <div
          style={{
            border: '2px dashed var(--border)',
            borderRadius: '8px',
            padding: '40px 20px',
            textAlign: 'center',
            background: 'var(--background)',
            cursor: 'pointer',
            transition: 'border-color 0.2s',
            marginBottom: '20px'
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = 'var(--primary)';
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = 'var(--border)';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = 'var(--border)';
            const file = e.dataTransfer.files?.[0];
            if (file) {
              parseExcelFile(file);
            }
          }}
        >
          <input
            type="file"
            accept=".xlsx, .xls, .csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="excel-file-upload"
          />
          <label htmlFor="excel-file-upload" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <Upload size={32} style={{ color: 'var(--primary)' }} />
            <div>
              <p style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>
                Arrastra y suelta tu archivo de Excel aquí o haz clic para buscarlo
              </p>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                Formatos soportados: .xlsx, .xls, .csv
              </p>
            </div>
          </label>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '16px', marginBottom: '20px', background: 'var(--surface)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h4 style={{ fontWeight: 600, fontSize: '14px' }}>Archivo cargado: <span style={{ color: 'var(--primary)' }}>{fileName}</span></h4>
              <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '12px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Total: <strong>{parsedRows.length}</strong></span>
                <span style={{ color: 'var(--success)' }}>Válidos: <strong>{validCount}</strong></span>
                {invalidCount > 0 && <span style={{ color: 'var(--danger)' }}>Errores: <strong>{invalidCount}</strong></span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary" onClick={handleCancel} disabled={isLoading} style={{ border: '1px solid var(--border)' }}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleConfirmImport} disabled={validCount === 0 || isLoading}>
                {isLoading ? 'Importando...' : `Confirmar e Importar (${validCount})`}
              </button>
            </div>
          </div>

          {/* Table Preview */}
          <div style={{ maxHeight: '350px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '6px' }}>
            <table className="table" style={{ margin: 0 }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 10 }}>
                <tr>
                  <th style={{ width: '60px', textAlign: 'center' }}>Estado</th>
                  {headers.map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row, idx) => (
                  <tr key={idx} style={{ background: row.isValid ? 'transparent' : 'rgba(244, 63, 94, 0.04)' }}>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      {row.isValid ? (
                        <CheckCircle size={16} style={{ color: 'var(--success)' }} />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <AlertCircle size={16} style={{ color: 'var(--danger)' }} />
                          <span
                            style={{
                              fontSize: '10px',
                              background: 'var(--danger)',
                              color: 'white',
                              borderRadius: '10px',
                              padding: '2px 6px',
                              fontWeight: 600,
                              cursor: 'help'
                            }}
                            title={row.errors.join('\n')}
                          >
                            {row.errors.length}
                          </span>
                        </div>
                      )}
                    </td>
                    {headers.map(col => {
                      let val = row.original[col];
                      if (val === undefined || val === null) val = '';
                      
                      if (col === 'Fecha Inicio' || col === 'Fecha Fin') {
                        if (val instanceof Date) {
                          val = val.toLocaleDateString();
                        } else if (typeof val === 'number') {
                          const date = new Date((val - 25569) * 86400 * 1000);
                          val = date.toLocaleDateString();
                        }
                      }
                      
                      return (
                        <td key={col} style={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {String(val)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Notifications */}
      {error && (
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          padding: '12px 16px',
          background: 'var(--danger-light)',
          color: 'var(--danger-text)',
          borderRadius: '6px',
          fontSize: '13px',
          marginTop: '16px'
        }}>
          <AlertCircle size={18} />
          <div>{error}</div>
        </div>
      )}

      {successMsg && (
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          padding: '12px 16px',
          background: 'var(--success-light)',
          color: 'var(--success-text)',
          borderRadius: '6px',
          fontSize: '13px',
          marginTop: '16px'
        }}>
          <CheckCircle size={18} />
          <div>{successMsg}</div>
        </div>
      )}
    </div>
  );
}

export default Settings;
